/**
 * Login page with email/password form, error feedback, and links
 * to the registration and forgot-password flows.
 *
 * @module LoginPage
 */

import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  Link,
  alpha,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { motion } from 'framer-motion';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useAuth } from '../contexts/AuthContext';
import { getErrorMessage } from '../api';
import { GREEN, DARK_BLUE } from '../theme';
import { FloatingOrb, PageTransition } from '../components/AnimatedComponents';

/** Renders the login form and handles authentication via the AuthContext. */
export default function LoginPage() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err: any) {
      setError(getErrorMessage(err, t('login.error')));
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageTransition>
      <SEO
        title={t('login.seoTitle')}
        description={t('login.seoDesc')}
        canonical="/login"
      />
      <Box
        sx={{
          minHeight: '80vh',
          display: 'flex',
          alignItems: 'center',
          py: 4,
          position: 'relative',
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${alpha(DARK_BLUE, 0.03)} 0%, ${alpha(GREEN, 0.04)} 100%)`,
        }}
      >
        <FloatingOrb size={400} color={GREEN} top={-100} right={-80} />
        <FloatingOrb size={300} color={DARK_BLUE} bottom={-80} left={-60} animationDelay="4s" />

        <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <Paper
              elevation={0}
              sx={{
                p: { xs: 4, md: 6 },
                boxShadow: '0 8px 40px rgba(0,0,0,0.08)',
                border: `1px solid ${alpha(DARK_BLUE, 0.06)}`,
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              {/* Top accent bar */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  background: `linear-gradient(90deg, ${GREEN}, ${DARK_BLUE})`,
                }}
              />

              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                >
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${GREEN}, ${DARK_BLUE})`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2.5,
                    }}
                  >
                    <LockIcon sx={{ color: 'white', fontSize: 28 }} />
                  </Box>
                </motion.div>
                <Typography variant="h3" sx={{ mb: 0.5, fontWeight: 700 }}>
                  {t('login.title')}
                </Typography>
                <Typography color="text.secondary" sx={{ fontSize: '1rem' }}>
                  {t('login.subtitle')}
                </Typography>
              </Box>

              {error && (
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
                  <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
                </motion.div>
              )}

              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label={t('login.email')}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  sx={{ mb: 2.5 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  label={t('login.password')}
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  sx={{ mb: 1 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Box sx={{ textAlign: 'right', mb: 3 }}>
                  <Link component={RouterLink} to="/forgot-password" variant="body2" sx={{ fontWeight: 500 }}>
                    {t('login.forgotPassword')}
                  </Link>
                </Box>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="secondary"
                  size="large"
                  disabled={loading}
                  sx={{ mb: 3, py: 1.5, borderRadius: 3, fontSize: '1rem' }}
                >
                  {loading ? t('login.submitting') : t('login.submit')}
                </Button>
              </form>

              <Typography align="center" variant="body2" color="text.secondary">
                {t('login.noAccount')}{' '}
                <Link component={RouterLink} to="/register" sx={{ fontWeight: 600 }}>
                  {t('login.signUp')}
                </Link>
              </Typography>
            </Paper>
          </motion.div>
        </Container>
      </Box>
    </PageTransition>
  );
}
